import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import { alpha } from '@mui/material/styles';
import Filters from '@IssueConsoleFunctionalDisplay/Filters';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import './Statistics.scss';

export default function Statistics(props) {
  const { currentUserProjectPermissions } = useReduxProject();
  const className = 'issueDashboards__statistics';
  const statuses = useMemo(() => props?.ticketStatusList || [], [props?.ticketStatusList]);
  const activeIds = props?.statusList || [];

  const handleToggle = (statusId) => {
    if (!currentUserProjectPermissions?.allowView) return;
    props?.renderStatusTickets && props.renderStatusTickets(statusId);
  };

  const onKeyToggle = (e, statusId) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle(statusId);
    }
  };

  return (
    <div className={className}>
      {/* Header */}
      <Box className={className + '__top'}>
        <Typography className={className + '__title'} component="h2">
          Filter by status
        </Typography>

        {/* Status Chips */}
        <Box className={className + '__chips'} role="list">
          {!statuses?.length &&
            [...Array(5)].map((_, i) => (
              <Skeleton key={i} variant="rounded" width={140} height={32} />
            ))}

          {statuses?.map((s) => {
            const id = s?.status?.id;
            const label = s?.status?.title || 'Unknown';
            const color = s?.status?.color || '#5f6368';
            const count = s?.count ?? 0;
            const isActive = activeIds.includes(id);

            // Removed Avatar/person icon; using a simple colored dot inside label
            const Dot = () => (
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: color,
                  display: 'inline-block',
                  flexShrink: 0
                }}
              />
            );

            return (
              <Tooltip
                key={id}
                title={`${label} (${count})`}
                enterDelay={500}
                slotProps={{
                  popper: {
                    sx: {
                      '& .MuiTooltip-tooltip': {
                        backgroundColor: 'var(--color-componentBackground)',
                        color: 'var(--color-text)',
                        backdropFilter: 'none',
                        border: '1px solid var(--color-darkBorder)',
                        boxShadow: '0 12px 24px var(--color-shadow)',
                        borderRadius: '8px',
                        fontSize: '0.8125rem',
                        fontWeight: 600
                      }
                    }
                  }
                }}
              >
                <Chip
                  role="button"
                  aria-pressed={isActive ? 'true' : 'false'}
                  tabIndex={0}
                  onKeyDown={(e) => onKeyToggle(e, id)}
                  onClick={() => handleToggle(id)}
                  // avatar removed
                  label={
                    <Box className={className + '__chipLabel'}>
                      <Dot />
                      <span>{label}</span>
                      <span className={className + '__chipCount'}>{count}</span>
                    </Box>
                  }
                  className={className + '__chip'}
                  sx={{
                    cursor: currentUserProjectPermissions?.allowView ? 'pointer' : 'not-allowed',
                    opacity: currentUserProjectPermissions?.allowView ? 1 : 0.6,
                    bgcolor: isActive ? alpha(color, 0.18) : 'var(--color-componentBackground)',
                    color: isActive ? 'var(--color-text)' : 'var(--color-primaryGrey)',
                    borderColor: isActive ? color : 'var(--color-darkBorder)',
                    '&:hover': {
                      borderColor: color,
                      bgcolor: alpha(color, 0.18)
                    }
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
      </Box>

      {/* Filters */}
      <Box
        className={
          className +
          '__bottom ' +
          (!currentUserProjectPermissions?.allowView ? 'disabled' : '')
        }
      >
        <Filters
          filters={props.filters}
          onFilterChange={props.onFilterChange}
          clearFilters={props.clearFilters}
          handleSearch={props.handleSearch}
          isTicketFilter
          statusList={props.statusList}
        />
      </Box>
    </div>
  );
}
